import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { InboxService } from 'src/app/shared/services/inbox.service';
import { NotificationApiService } from 'src/app/shared/services/notification-api.service';
import { ENotificationReferenceType, ENotificationType, INotification, INotificationReference } from 'src/app/shared/interfaces/notification.interface';

export enum EInboxNotificationType {
	COMMENT = 'comment',
	STAGE_UPDATE = 'stage_update',
	SHARED_ACTION = 'shared_action',
	RECURRENCE = 'recurrence',
	RETENTION = 'retention',
}

export enum EInboxFilter {
	UNREAD = 'unread',
	MENTIONS = 'mentions',
	OPPORTUNITY = 'opportunity',
	RECURRENCE = 'recurrence',
}

export interface IInboxNotification {
	id: string;
	type: EInboxNotificationType;
	actorName: string;
	actorInitials: string;
	title: string;
	description: string;
	isRead: boolean;
	isCritical: boolean;
	createdAt: Date;
	/** Entity the notification points to, used to navigate on click. */
	reference?: INotificationReference;
}

export interface IInboxGroup {
	label: string;
	notifications: IInboxNotification[];
}

interface IFilterOption {
	filter: EInboxFilter;
	label: string;
	translateLabel?: boolean;
}

/** Actor id used by the backend for system-generated notifications (e.g. automatic rejection). */
const SYSTEM_ACTOR_ID = 'system';
/** Fixed avatar label shown for the automatic-rejection system actor. */
const SYSTEM_ACTOR_INITIALS = 'RP';

@Component({
	selector: 'app-inbox-panel',
	standalone: true,
	imports: [CommonModule, MaterialModule, TablerIconsModule, TranslateModule],
	templateUrl: './inbox-panel.component.html',
	styleUrls: ['./inbox-panel.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class InboxPanelComponent implements OnInit, OnDestroy {
	@Input() sidebarWidth = 270;

	readonly EInboxNotificationType = EInboxNotificationType;
	readonly EInboxFilter = EInboxFilter;

	readonly statusOptions: IFilterOption[] = [
		{ filter: EInboxFilter.UNREAD, label: 'INBOX.FILTER.UNREAD', translateLabel: true },
	];

	readonly categoryOptions: IFilterOption[] = [
		{ filter: EInboxFilter.MENTIONS, label: 'INBOX.FILTER.CATEGORY.COMMENTS', translateLabel: true },
		{ filter: EInboxFilter.OPPORTUNITY, label: 'INBOX.FILTER.CATEGORY.OPPORTUNITY', translateLabel: true },
		{ filter: EInboxFilter.RECURRENCE, label: 'INBOX.FILTER.CATEGORY.RECURRENCE', translateLabel: true },
	];

	activeFilters = new Set<EInboxFilter>();
	selectedNotification: IInboxNotification | null = null;
	isLoading = false;
	notificationGroups: IInboxGroup[] = [];

	private streamSub: Subscription | null = null;

	constructor(
		private inboxService: InboxService,
		private notificationApiService: NotificationApiService,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.loadNotifications();
		this.streamSub = this.inboxService.newNotification$.subscribe(raw => this.prependNotification(raw));
	}

	ngOnDestroy(): void {
		this.streamSub?.unsubscribe();
	}

	private prependNotification(raw: INotification): void {
		const n = this.mapNotification(raw);
		const group = this.notificationGroups.find(g => g.label === 'Hoje');
		if (group) {
			group.notifications = [n, ...group.notifications];
		} else {
			this.notificationGroups = [{ label: 'Hoje', notifications: [n] }, ...this.notificationGroups];
		}
		this.notificationGroups = [...this.notificationGroups];
	}

	private loadNotifications(): void {
		this.isLoading = true;
		this.notificationApiService.list().subscribe({
			next: (response) => {
				const notifications = response.data.map(n => this.mapNotification(n));
				this.notificationGroups = this.groupByDate(notifications);
				this.inboxService.refreshUnreadCount();
				this.isLoading = false;
			},
			error: () => {
				this.isLoading = false;
			},
		});
	}

	private mapNotification(n: INotification): IInboxNotification {
		return {
			id: n._id,
			type: this.mapType(n.type),
			actorName: n.actorName,
			actorInitials: n.actorId === SYSTEM_ACTOR_ID ? SYSTEM_ACTOR_INITIALS : this.getInitials(n.actorName),
			title: n.title,
			description: n.body ?? '',
			isRead: n.readAt !== null,
			isCritical: n.type === ENotificationType.TRASH_RETENTION_FINAL_WARNING,
			createdAt: new Date(n.createdAt),
			reference: n.reference,
		};
	}

	private mapType(type: ENotificationType): EInboxNotificationType {
		switch (type) {
			case ENotificationType.MENTION: return EInboxNotificationType.COMMENT;
			case ENotificationType.RECURRENCE: return EInboxNotificationType.RECURRENCE;
			case ENotificationType.SHARED_ACTION: return EInboxNotificationType.SHARED_ACTION;
			case ENotificationType.SHARED_OPPORTUNITY: return EInboxNotificationType.SHARED_ACTION;
			case ENotificationType.REVOKED_OPPORTUNITY: return EInboxNotificationType.SHARED_ACTION;
			case ENotificationType.STATUS_UPDATE: return EInboxNotificationType.STAGE_UPDATE;
			case ENotificationType.TRASH_RETENTION_WARNING: return EInboxNotificationType.RETENTION;
			case ENotificationType.TRASH_RETENTION_FINAL_WARNING: return EInboxNotificationType.RETENTION;
			default: return EInboxNotificationType.COMMENT;
		}
	}

	private getInitials(name: string): string {
		if (!name) return '';
		return name.trim().split(/\s+/).slice(0, 2).map(p => p[0].toUpperCase()).join('');
	}

	private groupByDate(notifications: IInboxNotification[]): IInboxGroup[] {
		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const yesterday = new Date(today);
		yesterday.setDate(today.getDate() - 1);

		const buckets = new Map<string, IInboxNotification[]>();

		for (const n of notifications) {
			const d = new Date(n.createdAt.getFullYear(), n.createdAt.getMonth(), n.createdAt.getDate());
			let label: string;
			if (d.getTime() === today.getTime()) label = 'Hoje';
			else if (d.getTime() === yesterday.getTime()) label = 'Ontem';
			else label = n.createdAt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
			if (!buckets.has(label)) buckets.set(label, []);
			buckets.get(label)!.push(n);
		}

		return Array.from(buckets.entries()).map(([label, items]) => ({ label, notifications: items }));
	}

	get hasActiveFilters(): boolean {
		return this.activeFilters.size > 0;
	}

	get activeFilterList(): IFilterOption[] {
		return [...this.statusOptions, ...this.categoryOptions].filter(o => this.activeFilters.has(o.filter));
	}

	get filteredGroups(): IInboxGroup[] {
		if (this.activeFilters.size === 0) return this.notificationGroups;

		const filterUnread = this.activeFilters.has(EInboxFilter.UNREAD);
		const typeFilters = this.categoryOptions.map(o => o.filter).filter(f => this.activeFilters.has(f));

		return this.notificationGroups
			.map(g => ({
				...g,
				notifications: g.notifications.filter(n => {
					const passesStatus = !filterUnread || !n.isRead;
					const passesType = typeFilters.length === 0 || typeFilters.some(f => this.matchesType(n, f));
					return passesStatus && passesType;
				}),
			}))
			.filter(g => g.notifications.length > 0);
	}

	isActive(filter: EInboxFilter): boolean {
		return this.activeFilters.has(filter);
	}

	toggleFilter(filter: EInboxFilter): void {
		if (this.activeFilters.has(filter)) {
			this.activeFilters.delete(filter);
		} else {
			this.activeFilters.add(filter);
		}
		this.activeFilters = new Set(this.activeFilters);
	}

	removeFilter(filter: EInboxFilter): void {
		this.activeFilters.delete(filter);
		this.activeFilters = new Set(this.activeFilters);
	}

	clearFilters(): void {
		this.activeFilters = new Set();
	}

	markAllAsRead(): void {
		this.notificationApiService.readAll().subscribe({
			next: () => {
				this.notificationGroups.forEach(g => g.notifications.forEach(n => (n.isRead = true)));
				this.inboxService.setUnreadCount(0);
			},
		});
	}

	archiveAll(): void {
		this.notificationApiService.archiveAll().subscribe({
			next: () => {
				this.notificationGroups = [];
				this.inboxService.setUnreadCount(0);
			},
		});
	}

	close(): void {
		this.inboxService.close();
	}

	selectNotification(notification: IInboxNotification): void {
		this.selectedNotification = notification;

		if (!notification.isRead) {
			this.notificationApiService.read(notification.id).subscribe({
				next: () => {
					notification.isRead = true;
					this.inboxService.refreshUnreadCount();
				},
			});
		}

		this.navigateToReference(notification);
	}

	/**
	 * Navigates to the entity the notification points to and closes the inbox. Trash-retention
	 * notifications go to the trash screen. For an opportunity reference — recurrence notifications
	 * included — the kanban screen resolves the `?opportunity=<id>` deep link into the
	 * edit modal (`crm-opportunities-update`) or the read-only detail modal, so this always sends
	 * the user through that single path.
	 */
	private navigateToReference(notification: IInboxNotification): void {
		if (notification.type === EInboxNotificationType.RETENTION) {
			this.inboxService.close();
			this.router.navigate(['/registers/opportunity/trash']);
			return;
		}

		const reference = notification.reference;
		if (reference?.type !== ENotificationReferenceType.OPPORTUNITY || !reference.id) return;

		this.inboxService.close();
		this.router.navigate(['/registers/opportunity'], { queryParams: { opportunity: reference.id } });
	}

	private matchesType(n: IInboxNotification, filter: EInboxFilter): boolean {
		switch (filter) {
			case EInboxFilter.MENTIONS: return n.type === EInboxNotificationType.COMMENT;
			case EInboxFilter.OPPORTUNITY:
				return n.type === EInboxNotificationType.STAGE_UPDATE
					|| n.type === EInboxNotificationType.SHARED_ACTION
					|| n.type === EInboxNotificationType.RETENTION;
			case EInboxFilter.RECURRENCE: return n.type === EInboxNotificationType.RECURRENCE;
			default: return true;
		}
	}

	trackByGroup(_: number, group: IInboxGroup): string { return group.label; }
	trackByNotification(_: number, n: IInboxNotification): string { return n.id; }

	/**
	 * Builds the hover tooltip for a notification: its full text (author + title, plus description),
	 * so the content is readable even when the list truncates it. HTML tags in the title are stripped
	 * since the tooltip is plain text.
	 * @param n The notification to describe.
	 * @returns The tooltip text.
	 */
	notificationTooltip(n: IInboxNotification): string {
		const title = (n.title ?? '').replace(/<[^>]*>/g, '').trim();
		const heading = [n.actorName, title].filter(Boolean).join(' ');
		return n.description ? `${heading}\n${n.description}` : heading;
	}
}
